/**
 * Checks if the given argument is null.
 *
 * @param arg - The argument to check.
 * @returns True if the argument is null, false otherwise.
 */
export function isNull(arg: unknown): arg is null {
  return arg === null;
}

/**
 * Checks if the given argument is not null.
 *
 * @param arg - The argument to check.
 * @returns True if the argument is not null, false otherwise.
 */
export function isNotNull(arg: unknown): arg is NonNullable<unknown> {
  return arg !== null;
}

/**
 * Checks if the given argument is undefined.
 *
 * @param arg - The argument to check.
 * @returns A boolean indicating whether the argument is undefined.
 */
export function isUndefined(arg: unknown): arg is undefined {
  return typeof arg === 'undefined';
}

/**
 * Checks if the given argument is null or undefined.
 *
 * @param arg - The argument to check.
 * @returns A boolean indicating whether the argument is null or undefined.
 */
export function isNullOrUndefined(arg: unknown): arg is null | undefined {
  return isNull(arg) || isUndefined(arg);
}

/**
 * Checks if the given argument is not undefined.
 *
 * @param arg - The argument to check.
 * @returns True if the argument is not undefined, false otherwise.
 */
export function isNotUndefined(arg: unknown): arg is NonNullable<unknown> {
  return typeof arg !== 'undefined';
}

/**
 * Checks if the given argument is not null or undefined.
 *
 * @param arg - The argument to check.
 * @returns A boolean indicating whether the argument is not null or undefined.
 */
export function isNotNullOrUndefined(arg: unknown): arg is NonNullable<unknown> {
  return isNotNull(arg) && isNotUndefined(arg);
}

/**
 * Checks if the given argument is an object.
 *
 * @param arg - The argument to check.
 * @returns A boolean indicating whether the argument is an object.
 */
export function isObject(arg: unknown): arg is Record<string, unknown> {
  return typeof arg === 'object' && arg !== null && !Array.isArray(arg) && !isString(arg);
}

/**
 * Checks if the provided argument is an empty object.
 * @param arg - The argument to check.
 * @returns True if the argument is an empty object, false otherwise.
 */
export function isEmptyObject(arg: unknown): arg is Record<string, never> {
  return isNotNullOrUndefined(arg) && isObject(arg) && Object.keys(arg).length === 0;
}

/**
 * Checks if the given argument is a non-empty object.
 *
 * @param arg - The argument to check.
 * @returns A boolean indicating whether the argument is a non-empty object.
 */
export function isNotEmptyObject(arg: unknown): arg is Record<string, unknown> {
  return isNotNullOrUndefined(arg) && isObject(arg) && !isEmptyObject(arg);
}

/**
 * Checks if the given argument is an array.
 *
 * @param arg - The argument to check.
 * @returns A boolean indicating whether the argument is an array.
 */
export function isArray(arg: unknown): arg is Array<unknown> {
  return Array.isArray(arg);
}

/**
 * Checks if the given argument is an empty array.
 * @param arg - The argument to check.
 * @returns True if the argument is an empty array, false otherwise.
 */
export function isEmptyArray(arg: unknown): arg is Array<never> {
  return isArray(arg) && arg.length === 0;
}

/**
 * Checks if the provided argument is a non-empty array.
 *
 * @param arg - The argument to check.
 * @returns A boolean indicating whether the argument is a non-empty array.
 */
export function isNotEmptyArray(arg: unknown): arg is Array<any> {
  return isArray(arg) && !isEmptyArray(arg);
}

/**
 * Checks if the given argument is a null or empty object.
 * @param arg - The argument to check.
 * @returns A boolean indicating whether the argument is a null or empty object.
 */
export function isNullOrEmptyObject(arg: unknown): arg is Record<string, never> {
  return isUndefined(arg) || isNull(arg) || isEmptyObject(arg as object);
}

/**
 * Checks if the provided argument is a non-null and non-empty object.
 *
 * @param arg - The argument to check.
 * @returns A boolean indicating whether the argument is a non-null and non-empty object.
 */
export function isNotNullOrEmptyObject(arg: object): arg is Record<string, unknown> {
  return !isNullOrEmptyObject(arg);
}

/**
 * Checks if the provided argument is empty data.
 * @param arg - The argument to check.
 * @returns A boolean indicating whether the argument is empty data.
 */
export function isEmptyData(
  arg: unknown,
): arg is null | undefined | Record<string, never> | '' | [] {
  return (
    isNullOrUndefined(arg) || isNullOrEmptyObject(arg) || isEmptyArray(arg) || isEmptyString(arg)
  );
}

/**
 * Checks if the provided argument is not empty data.
 * @param arg - The argument to check.
 * @returns A boolean indicating whether the argument is not empty data.
 */
export function isNotEmptyData(
  arg: unknown,
): arg is Record<string, unknown> | Array<unknown> | string {
  return !isEmptyData(arg);
}

/**
 * Checks if the given argument is a string.
 *
 * @param arg - The argument to check.
 * @returns A boolean indicating whether the argument is a string or not.
 */
export function isString(arg: unknown): arg is string {
  return typeof arg === 'string';
}

/**
 * Checks if a string is empty or consists only of whitespace characters.
 * @param arg - The string to check.
 * @returns True if the string is empty or consists only of whitespace characters, false otherwise.
 */
export function isEmptyString(arg: unknown): arg is '' {
  return isString(arg) && arg.trim() === '';
}

/**
 * Checks if the provided argument is a non-empty string.
 * @param arg - The argument to check.
 * @returns A boolean indicating whether the argument is a non-empty string.
 */
export function isNotEmptyString(arg: unknown): arg is string {
  return isEmptyString(arg) === false;
}

/**
 * Checks if the given argument is a number.
 *
 * @param arg - The argument to check.
 * @returns A boolean indicating whether the argument is a number or not.
 */
export function isNumber(arg: unknown): arg is number {
  return typeof arg === 'number';
}

/**
 * Checks if the given argument is a boolean.
 *
 * @param arg - The argument to check.
 * @returns A boolean indicating whether the argument is a boolean or not.
 */
export function isBoolean(arg: unknown): arg is boolean {
  return typeof arg === 'boolean';
}

/**
 * Checks if the given argument is a function.
 *
 * @param arg - The argument to check.
 * @returns A boolean indicating whether the argument is a function or not.
 */
export function isFunction(arg: unknown): arg is () => unknown {
  return typeof arg === 'function';
}

export function openInNewTab(url: string): void {
  window.open(url, '_blank', 'noopener noreferrer');
}

/**
 * Creates and returns an HTMLAnchorElement with the specified URL and file name for downloading.
 * @param url - The URL of the file to be downloaded.
 * @param fileName - The name of the file to be downloaded.
 * @returns The created HTMLAnchorElement.
 */
export function prepareDownloadLink(url: string, fileName: string): HTMLAnchorElement {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';

  link.setAttribute('target', '_blank');
  link.setAttribute('rel', 'noopener noreferrer');

  document.body.appendChild(link);
  return link;
}

/**
 * Downloads a file from the specified URL with the given file name.
 * @param url - The URL of the file to download.
 * @param fileName - The name to be used for the downloaded file.
 */
export function downloadFile(url: any, fileName: string) {
  const tempLink = prepareDownloadLink(url, fileName);

  tempLink.click();

  setTimeout(() => {
    document.body.removeChild(tempLink);
  }, 200);
}

/**
 * Downloads a file from the provided blob and file name.
 * @param blob - The file content as a blob.
 * @param fileName - The name to be used for the downloaded file.
 */
export function downloadFileBlob(blob: Blob, fileName: string) {
  const tempLink = prepareDownloadLink(URL.createObjectURL(blob), fileName);

  tempLink.click();
  setTimeout(() => {
    document.body.removeChild(tempLink);
  }, 200);
}
